import { useParams, useNavigate } from 'react-router-dom'
import { App as AntdApp, Descriptions, Card, Space, Button, Typography, Tag, Spin, Input, Select, Modal, Alert, Tooltip, Switch, Checkbox } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useDraftDetail, useSaveDraft, useConfirmDraft, useAssignmentCandidates, useTriggerContext, useCreateZentaoTask, useAskMoreDraft, useRejectDraft } from '@/modules/draft/queries'
import { useQueueMessageMediaDownload } from '@/modules/message/queries'
import { useAuthStore } from '@/stores/authStore'
import ErrorResult from '@/components/feedback/ErrorResult'
import WechatConversation from '@/pages/messages/components/WechatConversation'
import { useState, useEffect, useRef } from 'react'
import type { AssignmentCandidate, CreateTaskCommandSnapshot, DraftDetail } from '@/types/draft'
import type { MessageMedia } from '@/types/message'

const { Text, Paragraph } = Typography
const { TextArea } = Input

type DraftSourceMessage = DraftDetail['source_messages'][number]
type AttachmentCandidate = {
  message: DraftSourceMessage
  media: MessageMedia
  selectable: boolean
  reason: string
}

function formatBytes(value: number | null) {
  if (!value || value <= 0) return '-'
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / 1024 / 1024).toFixed(1)} MB`
}

function mediaTypeLabel(value: string) {
  if (value === 'image') return '图片'
  if (value === 'file') return '文档'
  if (value === 'voice') return '语音'
  if (value === 'video') return '视频'
  return value || '未知'
}

function attachmentStatusText(media: MessageMedia) {
  if (media.download_url || media.content_available) return '已落地'
  if (media.download_status === 'failed') return '复制失败'
  if (media.download_status === 'pending' || media.download_status === 'downloading') return '处理中'
  return '未落地'
}

function chatrecordSourceLabel(media: MessageMedia) {
  const idx = media.metadata?.chatrecord_index
  const type = media.metadata?.chatrecord_type
  if (idx === undefined && !type) return ''
  const indexNumber = typeof idx === 'number' ? idx : Number(idx)
  const indexText = idx === undefined || Number.isNaN(indexNumber) ? '' : `合并转发第 ${indexNumber + 1} 条`
  return [indexText, type ? String(type) : ''].filter(Boolean).join(' / ')
}

function collectAttachmentCandidates(data: DraftDetail | null | undefined): AttachmentCandidate[] {
  if (!data) return []
  return data.source_messages.flatMap((message) =>
    message.media.map((media) => {
      const typeAllowed = media.media_type === 'image' || media.media_type === 'file'
      const readable = !!media.download_url || media.content_available === true
      return {
        message,
        media,
        selectable: typeAllowed && readable,
        reason: !typeAllowed ? '一期只上传图片和文档' : readable ? '' : '文件未落地或不可读',
      }
    }),
  )
}
export default function DraftReviewPage() {
  const { message } = AntdApp.useApp()
  const { draftId } = useParams<{ draftId: string }>()
  const navigate = useNavigate()
  const { data, isLoading, isError, error, refetch } = useDraftDetail(draftId || null)
  const saveDraftMutation = useSaveDraft()
  const confirmDraftMutation = useConfirmDraft()
  const createZentaoTaskMutation = useCreateZentaoTask()
  const askMoreDraftMutation = useAskMoreDraft()
  const rejectDraftMutation = useRejectDraft()
  const queueMediaDownload = useQueueMessageMediaDownload()
  const { data: candidates } = useAssignmentCandidates(draftId || null)
  const { data: triggerContext } = useTriggerContext(draftId || null)
  const actor = useAuthStore((s) => s.actor) || 'unknown'
  const hasPermission = useAuthStore((s) => s.hasPermission)

  const [taskTitle, setTaskTitle] = useState('')
  const [problemType, setProblemType] = useState('')
  const [needTicket, setNeedTicket] = useState<boolean | null>(null)
  const [isOutOfWarranty, setIsOutOfWarranty] = useState(false)
  const [priority, setPriority] = useState('')
  const [summary, setSummary] = useState('')
  const [humanAssignee, setHumanAssignee] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [askMoreModalOpen, setAskMoreModalOpen] = useState(false)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false)
  const [askMoreMissingInfoText, setAskMoreMissingInfoText] = useState('')
  const [askMoreSuggestedReply, setAskMoreSuggestedReply] = useState('')
  const [rejectReason, setRejectReason] = useState('')
  const [confirmNote, setConfirmNote] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [creating, setCreating] = useState(false)
  const [selectedCandidate, setSelectedCandidate] = useState<string>('')
  const [selectedExecutionId, setSelectedExecutionId] = useState<number | null>(null)
  const [selectedMediaUids, setSelectedMediaUids] = useState<string[]>([])
  const initialized = useRef(false)

  // derive from operator_actions
  const canSave = data?.operator_actions?.includes('update') ?? false
  const canConfirm = data?.operator_actions?.includes('confirm') ?? false
  const canReject = data?.operator_actions?.includes('reject') ?? false
  const canAskMore = data?.operator_actions?.includes('ask_more') ?? false
  const canCreateTask = data?.operator_actions?.includes('create_task') ?? false
  const confirmDraftLabel = data?.status === 'need_more_info' ? '补齐并确认草稿' : '确认草稿'

  // 从 data 初始化编辑值（仅首次加载）
  useEffect(() => {
    if (data && !initialized.current) {
      setTaskTitle(data.task_title)
      setProblemType(data.problem_type)
      setNeedTicket(data.need_ticket)
      setIsOutOfWarranty(data.is_out_of_warranty)
      setPriority(data.priority)
      setSummary(data.summary)
      setHumanAssignee(data.human_assignee || '')
      setAssignedTo(data.assigned_to || '')
      setSelectedExecutionId(data.execution_routing?.candidates[0]?.execution_id || data.execution_routing?.default_execution_id || null)
      initialized.current = true
    }
  }, [data])

  useEffect(() => {
    if (!createTaskModalOpen || !data) return
    const defaultSelected = collectAttachmentCandidates(data)
      .filter((item) => item.selectable)
      .map((item) => item.media.media_uid)
    setSelectedMediaUids(defaultSelected)
  }, [createTaskModalOpen, data])

  if (isLoading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>
  if (isError || !data) return <ErrorResult error={error} onRetry={() => refetch()} />

  // 初始化编辑值
  const title = taskTitle || data.task_title
  const currentProblemType = problemType || data.problem_type
  const currentNeedTicket = needTicket ?? data.need_ticket
  const currentPriority = priority || data.priority
  const currentSummary = summary || data.summary
  const currentHumanAssignee = humanAssignee || data.human_assignee || ''
  const currentAssignedTo = assignedTo || data.assigned_to || ''

  const buildCreateTaskPayload = (nextAssignedTo: string): Record<string, unknown> => ({
    confirmed_by: actor,
    human_assignee: currentHumanAssignee,
    assigned_to: nextAssignedTo,
    task_title: title,
    priority: currentPriority,
    summary: currentSummary,
    ...(selectedExecutionId ? { zentao_execution_id: selectedExecutionId } : {}),
    selected_media_uids: selectedMediaUids,
    attachment_upload_mode: selectedMediaUids.length > 0 ? 'selected' : 'none',
    attachment_required: false,
  })

  const findCandidate = (value: string) => {
    const normalized = value.trim()
    if (!normalized) {
      return undefined
    }
    return candidates?.find(
      (candidate) =>
        candidate.account === normalized
        || candidate.internal_account === normalized
        || candidate.zentao_account === normalized,
    )
  }

  const formatCandidateLabel = (candidate: AssignmentCandidate) => {
    const displayAccount = candidate.internal_account || candidate.account
    const zentaoSuffix = candidate.zentao_account && candidate.zentao_account !== displayAccount
      ? ` / 禅道:${candidate.zentao_account}`
      : ''
    const validitySuffix = !candidate.valid ? ' - 无效' : ''
    const riskSuffix = candidate.risk_flags.length > 0 ? ` - ${candidate.risk_flags.join(', ')}` : ''
    return `${candidate.realname} (${displayAccount}${zentaoSuffix})${validitySuffix}${riskSuffix}`
  }

  const sourceTagLabel: Record<string, string> = {
    binding_tech_owner: '默认首派研发',
    suggested_assignee: 'AI 建议',
    execution_member: '当前执行成员',
    historical_closed: '历史处理',
    configured_internal: '系统研发',
  }

  // 选中候选人的有效性
  const selectedCand: AssignmentCandidate | undefined = findCandidate(selectedCandidate || currentAssignedTo)
  const resolvedAssignedTo = selectedCand?.account || currentAssignedTo
  const hasValidAssignee = !!selectedCand && selectedCand.valid !== false
  const attachmentCandidates = collectAttachmentCandidates(data)
  const selectableAttachmentUids = attachmentCandidates
    .filter((item) => item.selectable)
    .map((item) => item.media.media_uid)

  // 给 Modal 的研发候选选项
  const candidateOptions = (candidates || []).map((c) => ({
    label: formatCandidateLabel(c),
    value: c.account,
    disabled: !c.valid,
  }))

  // 快照
  const snapshot = data?.draft_payload?.create_task_command_snapshot as CreateTaskCommandSnapshot | undefined
  const executionRouting = data.execution_routing
  const executionOptions = executionRouting
    ? [
        ...executionRouting.candidates.map((execution) => ({
          label: `${execution.name} (#${execution.execution_id})`,
          value: execution.execution_id,
        })),
        ...(executionRouting.default_execution_id > 0 && !executionRouting.candidates.some((execution) => execution.execution_id === executionRouting.default_execution_id)
          ? [{ label: `默认执行 (#${executionRouting.default_execution_id})`, value: executionRouting.default_execution_id }]
          : []),
      ]
    : []
  const routingStatusText: Record<string, string> = {
    multiple_matches: '多个候选需选择',
    matched_existing: '命中已有执行',
    will_create_execution: '建单时创建执行',
    default_fallback_available: '使用默认执行兜底',
    unresolved: '待解析',
  }
  const routingStatusColor: Record<string, string> = {
    multiple_matches: 'warning',
    matched_existing: 'success',
    will_create_execution: 'processing',
    default_fallback_available: 'warning',
    unresolved: 'error',
  }

  // 保存当前编辑内容
  const savePayload = (nextAssignedTo = resolvedAssignedTo) => ({
    task_title: title,
    problem_type: currentProblemType,
    need_ticket: currentNeedTicket,
    is_out_of_warranty: isOutOfWarranty,
    priority: currentPriority,
    summary: currentSummary,
    human_assignee: currentHumanAssignee,
    suggested_assignee: nextAssignedTo,
  })

  return (
    <div data-testid="draft-review-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/drafts')}>返回列表</Button>
        <Text strong style={{ fontSize: 16 }}>草稿审核: {data.draft_no}</Text>
        <Tag color="processing">{data.status}</Tag>
      </Space>

      {/* 触发来源 */}
      {(data.trigger_source || triggerContext) && (
        <Card size="small" title="触发来源">
          <Descriptions size="small" column={2}>
            {data.trigger_source?.source_type === 'wechat_group_trigger' && (
              <Descriptions.Item label="来源标记">
                <Tag color="processing">微信群触发</Tag>
              </Descriptions.Item>
            )}
            {triggerContext?.room_id && (
              <Descriptions.Item label="群聊ID">
                <Text code>{triggerContext.room_id}</Text>
              </Descriptions.Item>
            )}
            {triggerContext?.trigger_key && (
              <Descriptions.Item label="Trigger Key">
                <Text code>{triggerContext.trigger_key}</Text>
              </Descriptions.Item>
            )}
            {triggerContext?.trigger_message && (
              <Descriptions.Item label="触发消息">
                <Text>{triggerContext.trigger_message.content_preview}</Text>
              </Descriptions.Item>
            )}
            {triggerContext?.context_message && (
              <Descriptions.Item label="合并转发摘要">
                <Text>{triggerContext.context_message.content_preview}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      {data.trigger_source && !triggerContext && (
        <Card size="small" title="触发来源（基础字段）">
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="来源标记">
              <Tag color="processing">微信群触发</Tag>
            </Descriptions.Item>
            {data.trigger_source.trigger_message_id && (
              <Descriptions.Item label="Trigger Message ID">
                <Text code>{data.trigger_source.trigger_message_id}</Text>
              </Descriptions.Item>
            )}
            {data.trigger_source.context_message_id && (
              <Descriptions.Item label="Context Message ID">
                <Text code>{data.trigger_source.context_message_id}</Text>
              </Descriptions.Item>
            )}
            {data.trigger_source.trigger_key && (
              <Descriptions.Item label="Trigger Key">
                <Text code>{data.trigger_source.trigger_key}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      <Alert
        type={isOutOfWarranty ? 'warning' : 'success'}
        showIcon
        title={`项目保修状态：${isOutOfWarranty ? '已过保' : '未过保'}`}
        description="该状态会保存到工单属性，并在创建禅道工单时追加到标题末尾。"
      />

      <div className="draft-review-cards-2col">
        {/* 来源消息 */}
        <Card size="small" title="来源消息与上下文">
          <WechatConversation
            messages={data.source_messages}
            pendingMediaUid={
              queueMediaDownload.isPending ? queueMediaDownload.variables?.mediaUid : undefined
            }
            onQueueDownload={(messageId, mediaUid) => {
              queueMediaDownload.mutate(
                { messageId, mediaUid },
                {
                  onSuccess: () => {
                    message.success('已加入下载队列')
                    void refetch()
                  },
                },
              )
            }}
          />
        </Card>

        {/* 绑定信息 */}
        <Card size="small" title="绑定信息">
          {data.binding ? (
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="群名">{data.binding.room_name}</Descriptions.Item>
              <Descriptions.Item label="客户">{data.binding.customer_name}</Descriptions.Item>
              <Descriptions.Item label="项目">{data.binding.project_name}</Descriptions.Item>
              <Descriptions.Item label="禅道产品">#{data.binding.zentao_product_id || '待审核'}</Descriptions.Item>
              <Descriptions.Item label="禅道项目">#{data.binding.zentao_project_id || '待审核'}</Descriptions.Item>
              <Descriptions.Item label="默认执行">#{data.binding.default_execution_id || data.binding.zentao_execution_id || '未设置'}</Descriptions.Item>
            </Descriptions>
          ) : (
            <Tag color="red">绑定缺失</Tag>
          )}
        </Card>
      </div>

      {executionRouting && (
        <Card size="small" title="执行路由">
          <Descriptions size="small" column={2}>
            <Descriptions.Item label="建议执行">
              {executionRouting.suggested_execution_name || <Text type="secondary">未识别</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="路由状态">
              <Tag color={routingStatusColor[executionRouting.resolution_status] || 'default'}>
                {routingStatusText[executionRouting.resolution_status] || executionRouting.resolution_status || '未知'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="绑定项目">#{executionRouting.zentao_project_id || '未设置'}</Descriptions.Item>
            <Descriptions.Item label="默认执行">#{executionRouting.default_execution_id || '未设置'}</Descriptions.Item>
          </Descriptions>
          {executionRouting.resolution_status === 'multiple_matches' && (
            <Alert
              type="warning"
              showIcon
              title="存在多个同名执行，创建禅道工单前必须在弹窗中人工选择目标执行。"
              style={{ marginTop: 8 }}
            />
          )}
          {executionRouting.candidates.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {executionRouting.candidates.map((execution) => (
                <Space key={execution.execution_id} wrap>
                  <Text strong>{execution.name}</Text>
                  <Text code>#{execution.execution_id}</Text>
                  <Text type="secondary">项目 #{execution.project_id}</Text>
                  <Tag>{execution.status}</Tag>
                </Space>
              ))}
            </div>
          ) : (
            <Alert
              type={executionRouting.resolution_status === 'will_create_execution' ? 'info' : 'warning'}
              showIcon
              title={executionRouting.resolution_status === 'will_create_execution'
                ? '未找到同名执行，创建禅道工单时将按建议名称创建执行。'
                : '未找到匹配执行，建单时将使用默认执行或要求补充路由信息。'}
              style={{ marginTop: 8 }}
            />
          )}
        </Card>
      )}

      {/* AI 判断 */}
      <Card size="small" title="AI 判断">
        <Space wrap>
          <span>置信度: <Tag color={data.confidence >= 0.8 ? 'green' : 'orange'}>{(data.confidence * 100).toFixed(0)}%</Tag></span>
          {data.risk_flags.map((f) => <Tag key={f} color="red">{f}</Tag>)}
        </Space>
      </Card>

      {/* 初始指派候选 */}
      {candidates && candidates.length > 0 && (
        <Card size="small" title="初始指派候选">
          <Space orientation="vertical" style={{ width: '100%' }}>
            <Select
              showSearch
              allowClear
              style={{ width: '100%' }}
              value={selectedCand?.account || currentAssignedTo || undefined}
              onChange={(v) => {
                setSelectedCandidate(v || '')
                setAssignedTo(v || '')
              }}
              placeholder="输入姓名或内部账号搜索已配置研发"
              options={candidateOptions}
              optionFilterProp="label"
            />
            {selectedCand ? (
              <Space wrap>
                <Text strong>{selectedCand.realname}</Text>
                <Text code>{selectedCand.internal_account || selectedCand.account}</Text>
                {selectedCand.zentao_account && selectedCand.zentao_account !== (selectedCand.internal_account || selectedCand.account) && (
                  <Text type="secondary">禅道: {selectedCand.zentao_account}</Text>
                )}
                <Tag>{sourceTagLabel[selectedCand.source] || selectedCand.source}</Tag>
                <Text type="secondary">{selectedCand.reason}</Text>
                <Tag color={selectedCand.valid ? 'green' : 'red'}>{selectedCand.valid ? '有效' : '无效'}</Tag>
                {selectedCand.risk_flags.map((f) => <Tag key={f} color="orange" style={{ fontSize: 11 }}>{f}</Tag>)}
              </Space>
            ) : (
              <Text type="secondary">共 {candidates.length} 个候选，可按姓名或账号搜索选择。</Text>
            )}
          </Space>
        </Card>
      )}

      {/* 工单内容编辑 */}
      <Card size="small" title="工单内容">
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>标题</Text>
            <Input value={title} onChange={(e) => setTaskTitle(e.target.value)} placeholder="工单标题" />
          </div>
          <div className="draft-review-form-4col">
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>优先级</Text>
              <Select value={currentPriority} onChange={setPriority} style={{ width: '100%' }}
                options={[{ label: 'P1-紧急', value: 'P1' }, { label: 'P2-高', value: 'P2' }, { label: 'P3-中', value: 'P3' }]} />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>问题类型</Text>
              <Select
                value={currentProblemType}
                onChange={setProblemType}
                style={{ width: '100%' }}
                options={[{ label: '现场故障', value: '现场故障' }, { label: '一般咨询', value: '一般咨询' }]}
              />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>生成禅道工单</Text>
              <Switch
                checked={currentNeedTicket}
                checkedChildren="需要"
                unCheckedChildren="不需要"
                onChange={setNeedTicket}
              />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>是否已过保</Text>
              <Switch
                checked={isOutOfWarranty}
                checkedChildren="已过保"
                unCheckedChildren="未过保"
                onChange={setIsOutOfWarranty}
              />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>客户确认跟进人</Text>
              <Input
                value={currentHumanAssignee}
                onChange={(e) => setHumanAssignee(e.target.value)}
                placeholder="负责同步客户确认和闭环的人"
              />
            </div>
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>禅道初始指派研发</Text>
            <Select
              showSearch
              allowClear
              style={{ width: '100%' }}
              value={selectedCand?.account || currentAssignedTo || undefined}
              onChange={(v) => setAssignedTo(v || '')}
              placeholder="选择已配置研发作为禅道初始指派人"
              options={candidateOptions}
              optionFilterProp="label"
            />
            {selectedCand && selectedCand.risk_flags.length > 0 && (
              <Alert
                type="warning"
                title={`风险提示：${selectedCand.risk_flags.join('、')}`}
                style={{ marginTop: 4, fontSize: 12 }}
              />
            )}
            {selectedCand && !selectedCand.valid && (
              <Alert type="error" title="所选研发当前无效，不能创建 task" style={{ marginTop: 4, fontSize: 12 }} />
            )}
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>摘要</Text>
            <TextArea rows={2} value={currentSummary} onChange={(e) => setSummary(e.target.value)} />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>已知事实</Text>
            {data.known_facts.length > 0 ? (
              <ul style={{ background: '#fafafa', padding: '8px 8px 8px 28px', borderRadius: 4, margin: 0, fontSize: 13 }}>
                {data.known_facts.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            ) : <Text type="secondary" style={{ fontSize: 13 }}>-</Text>}
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>待补信息</Text>
            {data.missing_info.length > 0 ? (
              <ul style={{ background: '#fffbe6', padding: '8px 8px 8px 28px', borderRadius: 4, margin: 0, fontSize: 13 }}>
                {data.missing_info.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            ) : <Text type="secondary" style={{ fontSize: 13 }}>-</Text>}
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>验收标准</Text>
            {data.acceptance_criteria.length > 0 ? (
              <ul style={{ background: '#fafafa', padding: '8px 8px 8px 28px', borderRadius: 4, margin: 0, fontSize: 13 }}>
                {data.acceptance_criteria.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            ) : <Text type="secondary" style={{ fontSize: 13 }}>-</Text>}
          </div>
        </Space>
      </Card>

      {/* 建单快照 */}
      {snapshot && (
        <Card size="small" title="建单 Payload 快照">
          <Descriptions size="small" column={2}>
            <Descriptions.Item label="禅道执行">{snapshot.zentao_execution_id}</Descriptions.Item>
            <Descriptions.Item label="优先级">{snapshot.priority}</Descriptions.Item>
            <Descriptions.Item label="问题类型">{snapshot.problem_type}</Descriptions.Item>
            <Descriptions.Item label="初始指派研发">{snapshot.assigned_to || '-'}</Descriptions.Item>
            <Descriptions.Item label="客户确认跟进人">{snapshot.human_assignee || '-'}</Descriptions.Item>
            <Descriptions.Item label="摘要" span={2}>{snapshot.summary || '-'}</Descriptions.Item>
          </Descriptions>
          {snapshot.known_facts && snapshot.known_facts.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>已知事实：</Text>
              <ul style={{ margin: '4px 0 0 16px', fontSize: 12 }}>
                {snapshot.known_facts.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
          )}
          {snapshot.missing_info && snapshot.missing_info.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <Text type="warning" style={{ fontSize: 12 }}>待补信息：</Text>
              <ul style={{ margin: '4px 0 0 16px', fontSize: 12, color: '#faad14' }}>
                {snapshot.missing_info.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
          )}
        </Card>
      )}

      {/* 建议回复 */}
      {data.suggested_reply && (
        <Card size="small" title="建议回复">
          <Paragraph style={{ background: '#f6ffed', padding: 12, borderRadius: 4, fontSize: 13 }}>
            {data.suggested_reply}
          </Paragraph>
        </Card>
      )}

      {/* 操作栏 */}
      <Card size="small">
        <Space wrap>
          <Button
            data-testid="draft-action-view-workflow"
            onClick={() => navigate(`/workflow-traces/draft/${data.id}`)}
          >
            查看流程
          </Button>
          {canSave && hasPermission('draft:update') && (
            <Button
              data-testid="draft-action-save"
              loading={saveDraftMutation.isPending}
              onClick={() => {
                saveDraftMutation.mutate({
                  draftId: data.id,
                  payload: savePayload(),
                }, {
                  onSuccess: () => {
                    message.success('草稿已保存')
                    void refetch()
                  },
                  onError: (err) => {
                    message.error(err instanceof Error ? err.message : '保存草稿失败')
                  },
                })
              }}
            >
              保存草稿
            </Button>
          )}
          {canAskMore && hasPermission('draft:update') && (
            <Button
              data-testid="draft-action-ask-more"
              onClick={() => {
                setAskMoreMissingInfoText(data.missing_info.join('\n'))
                setAskMoreSuggestedReply(data.suggested_reply || '')
                setAskMoreModalOpen(true)
              }}
            >
              追问信息
            </Button>
          )}
          {canReject && hasPermission('draft:reject') && (
            <Button
              data-testid="draft-action-reject"
              danger
              onClick={() => {
                setRejectReason('')
                setRejectModalOpen(true)
              }}
            >
              拒绝建单
            </Button>
          )}
          {canConfirm && hasPermission('draft:confirm') && (
            <Button
              data-testid="draft-action-confirm"
              type="primary"
              ghost
              onClick={() => setConfirmModalOpen(true)}
            >
              {confirmDraftLabel}
            </Button>
          )}
          {canCreateTask && hasPermission('ticket:sync') && (
            <Tooltip title={!hasValidAssignee ? '请先选择有效的禅道初始指派研发' : ''}>
              <Button
                data-testid="draft-action-create-task"
                type="primary"
                onClick={() => setCreateTaskModalOpen(true)}
                disabled={!hasValidAssignee}
              >
                创建禅道工单
              </Button>
            </Tooltip>
          )}
        </Space>
      </Card>

      <Modal
        title="追问信息"
        open={askMoreModalOpen}
        onOk={async () => {
          const missingInfo = askMoreMissingInfoText
            .split(/\r?\n/)
            .map((item) => item.trim())
            .filter(Boolean)
          if (missingInfo.length === 0) {
            message.error('请至少填写一条待补信息')
            return
          }
          try {
            await askMoreDraftMutation.mutateAsync({
              draftId: data.id,
              payload: {
                missing_info: missingInfo,
                suggested_reply: askMoreSuggestedReply.trim() || undefined,
              },
            })
            message.success('已转为待补信息')
            setAskMoreModalOpen(false)
            void refetch()
          } catch (err) {
            message.error(err instanceof Error ? err.message : '提交追问失败')
          }
        }}
        onCancel={() => setAskMoreModalOpen(false)}
        confirmLoading={askMoreDraftMutation.isPending}
        okText="提交追问"
        okButtonProps={{ 'data-testid': 'draft-ask-more-submit' }}
      >
        <div data-testid="draft-ask-more-modal">
          <Space orientation="vertical" style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              title="每行填写一条待补信息，提交后草稿会进入待补信息状态。"
            />
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>待补信息</Text>
              <TextArea
                data-testid="draft-ask-more-missing-info"
                rows={4}
                value={askMoreMissingInfoText}
                onChange={(e) => setAskMoreMissingInfoText(e.target.value)}
                placeholder="例如：请补充终端编号"
              />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>建议回复</Text>
              <TextArea
                rows={3}
                value={askMoreSuggestedReply}
                onChange={(e) => setAskMoreSuggestedReply(e.target.value)}
                placeholder="可选，补充给客户的追问口径"
              />
            </div>
          </Space>
        </div>
      </Modal>

      <Modal
        title="拒绝建单"
        open={rejectModalOpen}
        onOk={async () => {
          const reason = rejectReason.trim()
          if (!reason) {
            message.error('请填写拒绝原因')
            return
          }
          try {
            await rejectDraftMutation.mutateAsync({
              draftId: data.id,
              payload: { reason },
            })
            message.success('草稿已拒绝')
            setRejectModalOpen(false)
            void refetch()
          } catch (err) {
            message.error(err instanceof Error ? err.message : '拒绝建单失败')
          }
        }}
        onCancel={() => setRejectModalOpen(false)}
        confirmLoading={rejectDraftMutation.isPending}
        okText="确认拒绝"
        okButtonProps={{ danger: true, 'data-testid': 'draft-reject-submit' }}
      >
        <div data-testid="draft-reject-modal">
          <Space orientation="vertical" style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              title="拒绝后草稿会进入 rejected 状态，请填写可审计的拒绝原因。"
            />
            <TextArea
              data-testid="draft-reject-reason"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="请填写拒绝原因"
            />
          </Space>
        </div>
      </Modal>

      {/* 确认草稿弹窗 */}
      <Modal
        title={confirmDraftLabel}
        open={confirmModalOpen}
        onOk={async () => {
          setConfirming(true)
          try {
            await saveDraftMutation.mutateAsync({
              draftId: data.id,
              payload: savePayload(),
            })
            await confirmDraftMutation.mutateAsync({
              draftId: data.id,
              payload: {
                confirmed_by: actor,
                human_assignee: currentHumanAssignee,
                remark: confirmNote,
              },
            })
            setConfirmModalOpen(false)
            refetch()
          } catch (err) {
            message.error(err instanceof Error ? err.message : '确认草稿失败')
          } finally {
            setConfirming(false)
          }
        }}
        onCancel={() => setConfirmModalOpen(false)}
        confirmLoading={confirming}
      >
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Text>确认草稿后进入已确认状态，可继续创建禅道工单。请确认以下信息：</Text>
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="标题">{title}</Descriptions.Item>
            <Descriptions.Item label="优先级">{currentPriority}</Descriptions.Item>
            <Descriptions.Item label="问题类型">{currentProblemType}</Descriptions.Item>
            <Descriptions.Item label="生成禅道工单">{currentNeedTicket ? '需要' : '不需要'}</Descriptions.Item>
            <Descriptions.Item label="是否已过保">{isOutOfWarranty ? '已过保' : '未过保'}</Descriptions.Item>
            <Descriptions.Item label="客户确认跟进人">{currentHumanAssignee || '未指定'}</Descriptions.Item>
            <Descriptions.Item label="禅道初始指派研发">{resolvedAssignedTo || '未选择'}</Descriptions.Item>
          </Descriptions>
          <TextArea placeholder="确认备注（可选）" value={confirmNote} onChange={(e) => setConfirmNote(e.target.value)} />
        </Space>
      </Modal>

      {/* 创建禅道工单弹窗 */}
      <Modal
        title="创建禅道工单"
        open={createTaskModalOpen}
        onOk={async () => {
          if (!hasValidAssignee) return
          setCreating(true)
          const nextAssignedTo = selectedCand?.account || selectedCandidate || currentAssignedTo
          const createPayload = buildCreateTaskPayload(nextAssignedTo)
          try {
            await saveDraftMutation.mutateAsync({
              draftId: data.id,
              payload: savePayload(nextAssignedTo),
            })
            const result = await createZentaoTaskMutation.mutateAsync({
              draftId: data.id,
              payload: createPayload,
            })
            setCreateTaskModalOpen(false)
            navigate(`/tickets/${result.mapping_id}`)
          } catch (err) {
            message.error(err instanceof Error ? err.message : '创建禅道工单失败')
          } finally {
            setCreating(false)
          }
        }}
        onCancel={() => setCreateTaskModalOpen(false)}
        confirmLoading={creating}
        okButtonProps={{ disabled: !hasValidAssignee }}
      >
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Alert
            type="warning"
            title="创建后将生成禅道 task，请确认初始指派研发正确"
            style={{ marginBottom: 12 }}
          />
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="标题">{title}</Descriptions.Item>
            <Descriptions.Item label="优先级">{currentPriority}</Descriptions.Item>
            <Descriptions.Item label="问题类型">{currentProblemType}</Descriptions.Item>
            <Descriptions.Item label="是否已过保">{isOutOfWarranty ? '已过保' : '未过保'}</Descriptions.Item>
            <Descriptions.Item label="客户确认跟进人">{currentHumanAssignee || '未指定'}</Descriptions.Item>
            <Descriptions.Item label="工单执行">
              {executionOptions.length > 0 ? (
                <Select
                  style={{ width: '100%' }}
                  value={selectedExecutionId || undefined}
                  onChange={(value) => setSelectedExecutionId(value || null)}
                  options={executionOptions}
                />
              ) : (
                <Text type="secondary">
                  {executionRouting?.resolution_status === 'will_create_execution'
                    ? `建单时创建：${executionRouting.suggested_execution_name}`
                    : '建单时自动解析'}
                </Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="禅道初始指派研发">
              <Select
                showSearch
                style={{ width: '100%' }}
                value={selectedCand?.account || selectedCandidate || currentAssignedTo || undefined}
                onChange={(v) => {
                  setSelectedCandidate(v || '')
                  setAssignedTo(v || '')
                }}
                placeholder="必须选择有效研发"
                options={candidateOptions}
                optionFilterProp="label"
              />
            </Descriptions.Item>
          </Descriptions>
          <Card
            size="small"
            title="要上传到禅道的附件"
            extra={
              attachmentCandidates.length > 0 ? (
                <Space size={4} wrap>
                  <Button size="small" onClick={() => setSelectedMediaUids(selectableAttachmentUids)}>全选</Button>
                  <Button size="small" onClick={() => setSelectedMediaUids([])}>全不选</Button>
                  <Button
                    size="small"
                    onClick={() => setSelectedMediaUids(
                      attachmentCandidates
                        .filter((item) => item.selectable && item.media.media_type === 'image')
                        .map((item) => item.media.media_uid),
                    )}
                  >
                    只选图片
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setSelectedMediaUids(
                      attachmentCandidates
                        .filter((item) => item.selectable && item.media.media_type === 'file')
                        .map((item) => item.media.media_uid),
                    )}
                  >
                    只选文档
                  </Button>
                </Space>
              ) : null
            }
          >
            {attachmentCandidates.length === 0 ? (
              <Text type="secondary">来源消息没有可上传的图片或文档附件。</Text>
            ) : (
              <Space orientation="vertical" style={{ width: '100%' }}>
                {attachmentCandidates.map(({ message: sourceMessage, media, selectable, reason }) => {
                  const checked = selectedMediaUids.includes(media.media_uid)
                  const sourceLabel = chatrecordSourceLabel(media)
                  return (
                    <div
                      key={media.media_uid}
                      style={{
                        border: '1px solid #f0f0f0',
                        borderRadius: 6,
                        padding: 8,
                        background: selectable ? '#fff' : '#fafafa',
                      }}
                    >
                      <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space align="start">
                          <Checkbox
                            checked={checked}
                            disabled={!selectable}
                            onChange={(event) => {
                              const enabled = event.target.checked
                              setSelectedMediaUids((current) => {
                                if (enabled) {
                                  return current.includes(media.media_uid) ? current : [...current, media.media_uid]
                                }
                                return current.filter((uid) => uid !== media.media_uid)
                              })
                            }}
                          />
                          <Space orientation="vertical" size={2}>
                            <Space wrap>
                              <Text strong>{media.display_name || media.media_uid}</Text>
                              <Tag>{mediaTypeLabel(media.media_type)}</Tag>
                              <Tag color={selectable ? 'green' : 'default'}>{attachmentStatusText(media)}</Tag>
                              <Text type="secondary">{formatBytes(media.size_bytes)}</Text>
                            </Space>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              来源：{sourceMessage.sender_name || '-'} {sourceMessage.send_time || ''}
                              {sourceLabel ? `；${sourceLabel}` : ''}
                            </Text>
                            {!selectable && (
                              <Text type="warning" style={{ fontSize: 12 }}>{reason}</Text>
                            )}
                          </Space>
                        </Space>
                        <Space>
                          {media.download_url && (
                            <Button size="small" href={media.download_url} target="_blank" rel="noreferrer">
                              查看
                            </Button>
                          )}
                          {!selectable && (
                            <Button
                              size="small"
                              loading={queueMediaDownload.isPending && queueMediaDownload.variables?.mediaUid === media.media_uid}
                              onClick={() => {
                                queueMediaDownload.mutate(
                                  { messageId: sourceMessage.message_id, mediaUid: media.media_uid },
                                  {
                                    onSuccess: () => {
                                      message.success('已加入下载队列')
                                      void refetch()
                                    },
                                  },
                                )
                              }}
                            >
                              重新下载
                            </Button>
                          )}
                        </Space>
                      </Space>
                    </div>
                  )
                })}
              </Space>
            )}
            {attachmentCandidates.length > 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 8 }}
                title={`已选择 ${selectedMediaUids.length} / ${selectableAttachmentUids.length} 个可上传附件。未选择的附件仍保留在平台来源消息中。`}
              />
            )}
          </Card>
          {!hasValidAssignee && (
            <Alert type="error" title="未选择有效研发时不能创建禅道工单" />
          )}
        </Space>
      </Modal>
    </div>
  )
}
